import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../../components/BackOfficeMenu';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import { 
  ChevronLeft, 
  User, 
  Briefcase, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Shield, 
  Clock, 
  Star, 
  History, 
  StickyNote,
  Wallet,
  TrendingUp,
  FileText,
  Upload,
  Download,
  Trash
} from 'lucide-react';
import '../GestionRH.css';
import './DetailEmploye.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '—';
const fmtFull = (date) => date ? new Date(date).toLocaleString('fr-FR') : '—';

const TABS = [
  { id: 'infos', label: 'Informations', icon: <User size={16} /> },
  { id: 'salaires', label: 'Salaires', icon: <Wallet size={16} /> },
  { id: 'conges', label: 'Congés & Absences', icon: <Calendar size={16} /> },
  { id: 'evals', label: 'Évaluations', icon: <Star size={16} /> },
  { id: 'docs', label: 'Documents', icon: <FileText size={16} /> },
  { id: 'audit', label: 'Historique', icon: <History size={16} /> },
  { id: 'notes', label: 'Notes', icon: <StickyNote size={16} /> }
];

const MOIS_LABELS = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function DetailEmploye({ employeId, onNavigate, onLogout, currentPage }) {
  const { hasPermission } = usePermissions();
  const [employe, setEmploye] = useState(null);
  const [salaires, setSalaires] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('infos');

  useEffect(() => {
    if (!employeId) { onNavigate('rh-employes'); return; }
    fetchAll();
  }, [employeId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [empRes, salRes, demRes, evalRes, histRes] = await Promise.all([
        axios.get(`${API}/rh/employes/${employeId}`, { headers: headers() }),
        axios.get(`${API}/rh/salaires?employe=${employeId}`, { headers: headers() }),
        axios.get(`${API}/rh/demandes?employe=${employeId}`, { headers: headers() }),
        axios.get(`${API}/rh/evaluations?employe=${employeId}`, { headers: headers() }),
        axios.get(`${API}/rh/employes/${employeId}/historique`, { headers: headers() }),
      ]);
      setEmploye(empRes.data);
      setSalaires(Array.isArray(salRes.data) ? salRes.data : []);
      setDemandes(Array.isArray(demRes.data) ? demRes.data : []);
      setEvaluations(Array.isArray(evalRes.data) ? evalRes.data : []);
      setHistorique(Array.isArray(histRes.data) ? histRes.data : []);
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const anciennete = (dateDebut) => {
    if (!dateDebut) return '—';
    const ms = new Date() - new Date(dateDebut);
    const years = Math.floor(ms / (1000 * 60 * 60 * 24 * 365));
    const months = Math.floor((ms % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
    return years > 0 ? `${years} an(s) ${months} mois` : `${months} mois`;
  };

  if (loading) return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content"><LoadingSpinner /></div>
    </div>
  );

  if (error || !employe) return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content">
        <div className="rh-table-card" style={{ textAlign: 'center', padding: '50px' }}>
          <h2 style={{ color: 'var(--rh-danger)' }}>{error || 'Employé non trouvé'}</h2>
          <button className="rh-add-btn" style={{ margin: '20px auto' }} onClick={() => onNavigate('rh-employes')}>
            <ChevronLeft size={20} /> Retour à la liste
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="rh-dashboard-content">
        
        {/* Header Hero Section */}
        <div className="rh-detail-header animate-fade-in">
          <div className="rh-detail-identity">
            <button className="rh-detail-back-btn" onClick={() => onNavigate('rh-employes')}>
              <ChevronLeft size={18} /> Retour
            </button>
            <div className="rh-detail-avatar">
              {employe.prenom[0]}{employe.nom[0]}
            </div>
            <div className="rh-detail-info">
              <h1>{employe.prenom} {employe.nom}</h1>
              <p>{employe.poste?.nom || '—'} · {employe.departement?.nom || '—'}</p>
            </div>
          </div>
          <span className={`rh-status-badge ${employe.statut?.toLowerCase() || 'actif'}`}>
            {employe.statut}
          </span>
        </div>

        {/* Tabs Navigation */}
        <div className="rh-detail-tabs animate-fade-in">
          {TABS.map((tab) => (
            <button 
              key={tab.id} 
              className={`rh-detail-tab ${activeTab === tab.id ? 'active' : ''}`} 
              onClick={() => setActiveTab(tab.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {tab.icon}
                {tab.label}
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in">
          {activeTab === 'infos' && (
            <div className="rh-detail-grid">
              <div className="rh-detail-card">
                <h3><User size={18} /> Informations personnelles</h3>
                <div className="rh-detail-rows">
                  <div className="rh-detail-row"><span>CIN</span><strong>{employe.cin}</strong></div>
                  <div className="rh-detail-row"><span>Date de naissance</span><strong>{fmt(employe.dateNaissance)}</strong></div>
                  <div className="rh-detail-row"><span>Âge</span><strong>{employe.age ? `${employe.age} ans` : '—'}</strong></div>
                  <div className="rh-detail-row"><span>Téléphone</span><strong>{employe.telephone}</strong></div>
                  <div className="rh-detail-row"><span>Email</span><strong>{employe.email || '—'}</strong></div>
                  <div className="rh-detail-row"><span>Adresse</span><strong>{employe.adresse || '—'}</strong></div>
                </div>
              </div>

              <div className="rh-detail-card">
                <h3><Briefcase size={18} /> Contrat & Poste</h3>
                <div className="rh-detail-rows">
                  <div className="rh-detail-row"><span>Poste</span><strong>{employe.poste?.nom || '—'}</strong></div>
                  <div className="rh-detail-row"><span>Département</span><strong>{employe.departement?.nom || '—'}</strong></div>
                  <div className="rh-detail-row"><span>Début contrat</span><strong>{fmt(employe.dateDebutContrat)}</strong></div>
                  <div className="rh-detail-row"><span>Type contrat</span><strong>{employe.typeContrat || 'CDI'}</strong></div>
                  <div className="rh-detail-row"><span>Ancienneté</span><strong>{anciennete(employe.dateDebutContrat)}</strong></div>
                </div>
              </div>

              <div className="rh-detail-card">
                <h3><Shield size={18} /> Droits & Paramètres</h3>
                <div className="rh-detail-rows">
                  <div className="rh-detail-row"><span>Congés autorisés</span><strong>{employe.congesAutorises} j/an</strong></div>
                  <div className="rh-detail-row"><span>Maladies autorisées</span><strong>{employe.maladiesAutorisees} j/an</strong></div>
                  {employe.userLie && (
                    <div className="rh-detail-row">
                      <span>Compte utilisateur</span>
                      <strong>{employe.userLie.email}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'salaires' && (
            <div className="rh-table-card">
              <div className="rh-table-header">
                <h2>Historique des Salaires</h2>
                <button className="rh-add-btn" onClick={() => onNavigate('rh-salaires')}>
                  <Wallet size={16} /> Gérer la paie
                </button>
              </div>
              <div className="rh-table-container">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Période</th>
                      <th>Salaire Base</th>
                      <th>Primes</th>
                      <th>Déductions</th>
                      <th>Net à payer</th>
                      <th>Statut</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salaires.map(s => (
                      <tr key={s._id}>
                        <td><strong>{MOIS_LABELS[s.mois]} {s.annee}</strong></td>
                        <td>{s.salaireBase.toLocaleString('fr-TN')} TND</td>
                        <td><span className="rh-amount-pos">+{s.primesTotal?.toLocaleString('fr-TN') || '0'}</span></td>
                        <td><span className="rh-amount-neg">-{s.deductionsTotal?.toLocaleString('fr-TN') || '0'}</span></td>
                        <td><span className="rh-amount-total">{s.salaireNet.toLocaleString('fr-TN')} TND</span></td>
                        <td>
                          <span className={`rh-status-badge ${s.statut === 'paye' ? 'approved' : 'pending'}`}>
                            {s.statut}
                          </span>
                        </td>
                        <td>
                          <div className="rh-actions">
                            <button className="rh-action-btn edit" onClick={() => onNavigate('rh-salaire-detail', s._id)}>
                              <FileText size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {salaires.length === 0 && (
                      <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>Aucun historique de salaire trouvé.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'conges' && (
            <div className="rh-table-card">
              <div className="rh-table-header">
                <h2>Demandes de Congés & Absences</h2>
                <button className="rh-add-btn" onClick={() => onNavigate('rh-demandes')}>
                  <Calendar size={16} /> Gérer les demandes
                </button>
              </div>
              <div className="rh-table-container">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Période</th>
                      <th>Durée</th>
                      <th>Statut</th>
                      <th>Commentaire</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demandes.map(d => (
                      <tr key={d._id}>
                        <td><span className="rh-type-badge">{d.type}</span></td>
                        <td>Du {fmt(d.dateDebut)} au {fmt(d.dateFin)}</td>
                        <td><strong>{d.nbJoursConsommes} j</strong></td>
                        <td>
                          <span className={`rh-status-badge ${d.statut === 'validee' ? 'approved' : d.statut === 'rejetee' ? 'rejected' : 'pending'}`}>
                            {d.statut}
                          </span>
                        </td>
                        <td><span style={{ fontSize: '13px', color: 'var(--rh-text-muted)' }}>{d.commentaireRH || '—'}</span></td>
                      </tr>
                    ))}
                    {demandes.length === 0 && (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>Aucune demande enregistrée.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'evals' && (
            <div className="rh-table-card">
              <div className="rh-table-header">
                <h2>Évaluations de Performance</h2>
                <button className="rh-add-btn" onClick={() => onNavigate('rh-evaluations')}>
                  <Star size={16} /> Gérer les évaluations
                </button>
              </div>
              <div className="rh-table-container">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Période</th>
                      <th style={{ width: '200px' }}>Score Final</th>
                      <th>Statut</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluations.map(e => (
                      <tr key={e._id}>
                        <td><strong>{MOIS_LABELS[e.mois]} {e.annee}</strong></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="rh-progress-container">
                              <div className="rh-progress-fill" style={{ width: `${e.scoreFinal}%` }} />
                            </div>
                            <span style={{ fontWeight: '700' }}>{e.scoreFinal}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`rh-status-badge ${e.statut === 'finalisee' ? 'approved' : 'pending'}`}>
                            {e.statut}
                          </span>
                        </td>
                        <td>{fmt(e.updatedAt || e.createdAt)}</td>
                      </tr>
                    ))}
                    {evaluations.length === 0 && (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '30px' }}>Aucune évaluation enregistrée.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="rh-detail-card">
              <h3><History size={18} /> Historique des modifications</h3>
              <div className="rh-audit-timeline">
                {historique.map((item, i) => (
                  <div key={i} className="rh-audit-item">
                    <div className="rh-audit-dot" />
                    <div className="rh-audit-content">
                      <div className="rh-audit-action">{item.details}</div>
                      <div className="rh-audit-meta">
                        Par {item.auteur?.prenom} {item.auteur?.nom} · {fmtFull(item.date)}
                      </div>
                    </div>
                  </div>
                ))}
                {historique.length === 0 && <p className="rh-empty">Aucun historique disponible.</p>}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="rh-detail-card">
              <h3><StickyNote size={18} /> Notes internes</h3>
              <div className="rh-notes-grid">
                {employe.notes?.map((note, i) => (
                  <div key={i} className="rh-note-card">
                    <p className="rh-note-text">{note.texte}</p>
                    <div className="rh-note-meta">
                      <span>{note.auteur?.prenom} {note.auteur?.nom}</span>
                      <span>{fmtFull(note.date)}</span>
                    </div>
                  </div>
                ))}
                {(!employe.notes || employe.notes.length === 0) && (
                  <p className="rh-empty">Aucune note enregistrée pour cet employé.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'docs' && (
            <div className="rh-table-card">
              <div className="rh-table-header">
                <h2>Documents de l'employé</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="rh-add-btn" onClick={() => {
                    Swal.fire({
                      title: 'Ajouter un document',
                      html: `
                        <div class="rh-modal-form">
                          <div class="rh-input-group">
                            <label>Nom du document</label>
                            <input id="doc-name" class="rh-modal-input" placeholder="Ex: CIN, Contrat, CV...">
                          </div>
                          <div class="rh-input-group">
                            <label>Fichier (URL)</label>
                            <input id="doc-url" class="rh-modal-input" placeholder="URL du document (PDF ou Image)">
                          </div>
                          <div class="rh-input-group">
                            <label>Type</label>
                            <select id="doc-type" class="rh-modal-input">
                              <option value="pdf">PDF</option>
                              <option value="image">Image</option>
                              <option value="autre">Autre</option>
                            </select>
                          </div>
                        </div>
                      `,
                      showCancelButton: true,
                      confirmButtonText: 'Ajouter',
                      preConfirm: () => {
                        const nom = document.getElementById('doc-name').value;
                        const url = document.getElementById('doc-url').value;
                        const type = document.getElementById('doc-type').value;
                        if (!nom || !url) {
                          Swal.showValidationMessage("Le nom et l'URL sont obligatoires");
                          return false;
                        }
                        return { nom, url, type };
                      }
                    }).then(async (result) => {
                      if (result.isConfirmed) {
                        try {
                          const updatedDocs = [...(employe.documents || []), result.value];
                          await axios.put(`${API}/rh/employes/${employeId}`, { documents: updatedDocs }, { headers: headers() });
                          Swal.fire('Succès', 'Document ajouté', 'success');
                          fetchAll();
                        } catch (err) {
                          Swal.fire('Erreur', "Impossible d'ajouter le document", 'error');
                        }
                      }
                    });
                  }}>
                    <Upload size={16} /> Ajouter un document
                  </button>
                </div>
              </div>
              <div className="rh-table-container">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Nom du document</th>
                      <th>Type</th>
                      <th>Date d'ajout</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employe.documents?.map((doc, i) => (
                      <tr key={i}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FileText size={20} color="var(--rh-primary)" />
                            <strong>{doc.nom}</strong>
                          </div>
                        </td>
                        <td><span className="rh-type-badge">{doc.type?.toUpperCase()}</span></td>
                        <td>{fmt(doc.dateAjout)}</td>
                        <td>
                          <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="rh-action-btn edit" title="Voir / Télécharger">
                              <Download size={16} />
                            </a>
                            <button className="rh-action-btn delete" title="Supprimer" onClick={async () => {
                              const confirm = await Swal.fire({
                                title: 'Supprimer ce document ?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Oui, supprimer',
                                confirmButtonColor: 'var(--rh-danger)'
                              });
                              if (confirm.isConfirmed) {
                                try {
                                  const updatedDocs = employe.documents.filter((_, idx) => idx !== i);
                                  await axios.put(`${API}/rh/employes/${employeId}`, { documents: updatedDocs }, { headers: headers() });
                                  Swal.fire('Supprimé', '', 'success');
                                  fetchAll();
                                } catch (err) {
                                  Swal.fire('Erreur', 'Impossible de supprimer le document', 'error');
                                }
                              }
                            }}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!employe.documents || employe.documents.length === 0) && (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '30px' }}>Aucun document disponible.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
